import { useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import axios from 'axios';

import { API_URL, MESSAGE } from 'constant';
import { loginState, WebTokenState } from 'global-stores';
import { REACT_APP_API_HOST as API_HOST } from 'envs';
import { useCookie, useNotification } from 'hooks';
import { useRetryOperation } from 'utils';

import './mock-api';

interface IConfig {
	headers?: {
		Authorization?: string;
		'Content-Type'?: string;
		'Cache-Control'?: string;
		accountMode?: string;
	};
	signal?: AbortSignal;
}
interface IProps {
	updateState?: boolean;
	returnResponse?: boolean;
}

export const useNetwork = (
	{ updateState = true, returnResponse = false }: IProps = {
		updateState: true,
		returnResponse: false,
	}
) => {
	const { accessToken: token } = useRecoilValue(loginState);
	const { deleteCookie } = useCookie();
	const { errorNotification } = useNotification();
	const { retryOperation } = useRetryOperation();

	const { get: getCookie } = useCookie();
	const localUserData = getCookie('user');
	const webToken = useRecoilValue(WebTokenState);
	const accessToken =
		webToken?.length > 0 ? webToken : token || localUserData?.accessToken;
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<any>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [status, setStatus] = useState<any>(null);
	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const [response, setResponse] = useState<any>(null);
	const [cancelTokenSource, setCancelTokenSource] = useState<any>();
	const [fileUploading, setFileOploading] = useState(0);
	const { sandboxStatus } = useRecoilValue(loginState);
	const sandbox = sandboxStatus ?? localUserData?.sandboxStatus;
	const config: IConfig = useMemo(() => ({}), []);
	const postConfig: IConfig = useMemo(() => ({}), []);

	// pradeep : AbortController implemented for aborting running api call if needed
	const abortController = new AbortController();
	const { signal } = abortController ?? {};

	if (accessToken) {
		config.headers = {
			Authorization: `Bearer ${accessToken}`,
			accountMode: sandbox ? 'sandbox' : 'live',
		};
	}
	config.headers = {
		...(config.headers ?? {}),
		'Cache-Control': 'no-cache',
	};
	config.signal = signal;

	postConfig.headers = {
		'Content-Type': 'application/json',
		'Cache-Control': 'no-cache',
		...(config.headers ?? {}),
	};

	/* this function is use to stop the file uploading process,
	   by this it will cancel the ongoing file upload Api call. */
	const stopUploadingFile = useCallback(() => {
		if (cancelTokenSource) {
			cancelTokenSource.cancel('file Uploading cancelled');
		}
	}, [cancelTokenSource]);

	const get = useCallback(
		async (url: string, configHeader?: any): Promise<any> => {
			setLoading(true);
			try {
				const response = await retryOperation(
					() =>
					 	axios.get(
							API_HOST + url,
							configHeader ?? config
						)
				);
				const { data: apiPayload } = response;
				if (returnResponse) {
					return { response, apiPayload };
				}
				if (updateState) {
					setStatus(response?.status);
					setIsLoaded(true);
					setData(apiPayload);
				}
				return apiPayload;
			} catch (err: any) {
				// This code has been commented out for now because we are handling this functionality update-user-token hooks. We might use it in the future, so it has been retained here as a reference.
				// if (err?.response?.status === 401 || err?.response?.status === 403) {
				// 	await moveToLoginPage();
				// 	deleteCookie('user');
				// 	deleteCookie('userEmail');
				// 	deleteRefreshCookie("auth_refresh_token");
				// 	localStorage.clear();
				// 	window.location.href = '/login';
				// }
				if (returnResponse) {
					return {
						errorCode: err?.response?.status ?? 500 | 400,
						errorData: err?.response?.data ?? {
							message: MESSAGE.ERROR,
						},
					};
				}
				if (updateState) {
					setError(err);
				}
				// shahbaaz: hide toaster becasue initially user don't have compliance Data
				// __simplici_compliance_toast_path__: compare the PATH, not the whole
				// url. getComplianceSessionStatus calls this endpoint as
				// '/business-compliance?check=true&accountType=...', so an exact match
				// stopped applying the moment a caller passed parameters -- leaving
				// "400: No data found" to reach the user for a business that has simply
				// not started onboarding yet.
				if (
					String(url).split('?')[0] === API_URL.BUSINESS_COMPLIANCE &&
					err?.response?.status === 400
				) {
					return;
				}

				// A session with no CIP record is a NORMAL state, not an error.
				// The detail modal fetches /onboarding-cip/:id unconditionally when
				// it opens, so every session that predates tiered onboarding -- which
				// is most of them -- popped "404: not found" over the panel the
				// moment a reviewer opened it. useOnboardingCip already treats the
				// failure as "no CIP" and renders nothing; only this toast made it
				// look like something had gone wrong. Prefix match because the raw
				// export calls the same path with `?include=raw`.
				if (
					(String(url).split('?')[0] ?? '').startsWith(API_URL.ONBOARDING_CIP) &&
					err?.response?.status === 404
				) {
					return null;
				}

				// A CASE WITH NO ACTIVITY HAS NO SAR DRAFT, and that is a normal
				// state rather than a failure. The case drawer asks for a draft
				// whenever it opens, and a case with no pinned transactions and no
				// linked alerts answers "Nothing matched that id for this business"
				// — which popped over the panel as an error for a case a reviewer
				// had merely opened. The drawer already renders the empty state and
				// says what to do about it (pin evidence, link an alert); only this
				// toast made it look like something had broken.
				if (String(url).split('?')[0] === API_URL.TM_SAR_DRAFTS) {
					return null;
				}

				// Transaction monitoring reads on a TIMER, so a single unreachable
				// poll must not stack toasts over the page. Both decks already say
				// so in place -- the control strip shows "refresh failed, showing
				// the last good page" and the table says "Could not reach
				// transaction monitoring" -- which is the honest, non-modal way to
				// report it. A toast per poll would bury the page in seconds.
				//
				// Reads only -- and that is guaranteed by WHERE this sits, not by a
				// method check: this catch belongs to `get`. A failed DECISION goes
				// through `patch`, whose own handler is untouched, because an
				// analyst who believes they just approved something has to be told
				// when it did not land.
				if ((String(url).split('?')[0] ?? '').startsWith('/transaction-monitoring/')) {
					return null;
				}

				if (err?.response?.status) {
                    const errorMessage = err?.response?.data?.message?.toLowerCase();
                    if (errorMessage?.includes('logged in required') || errorMessage?.includes('Unauthorized user.')) {
                        return null;
                    } else {
                        // Default behavior for other errors
                        errorNotification(
                            `${err.response.status}: ${errorMessage ?? MESSAGE.ERROR}`
                        );
                    }
				}
				return null;
			} finally {
				if (updateState) {
					setLoading(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[config, errorNotification, returnResponse, updateState]
	);

	const post = useCallback(
		async (url: string, requestJSON: any, configHeader?: any) => {
			if (configHeader) {
				postConfig.headers = {
					...postConfig.headers,
					...configHeader,
				};
			}
			if (updateState) {
				setLoading(true);
			}
			try {
				const response = await retryOperation(
					() =>
					 	axios.post(
							API_HOST + url, requestJSON, {
								...postConfig,
							}
						),
				);
				const { data: apiPayload } = response;
				setResponse(response);
				if (response.status === 500) {
					if (updateState) {
						setError(response.type);
					}
				}
				if (updateState) {
					setStatus(response?.ok);
				}
				if (url.includes('charts?businessId')) {
					if (updateState) {
						setIsLoaded(true);
						setData(apiPayload);
					}
					return apiPayload;
				}
				const apiResponse = apiPayload.data ?? apiPayload;

				if (updateState) {
					setIsLoaded(true);
					setData(apiResponse);
				}
				return apiResponse;
			} catch (err: any) {
				// This code has been commented out for now because we are handling this functionality update-user-token hooks. We might use it in the future, so it has been retained here as a reference.
				// if (err?.response?.status === 401 || err?.response?.status === 403) {
				// 	await moveToLoginPage();
				// 	deleteCookie('user');
				// 	deleteCookie('userEmail');
				// 	deleteRefreshCookie("auth_refresh_token");
				// 	localStorage.clear();
				// 	window.location.href = '/login';
				// }
				if (updateState) {
					setError(err);
				}
				if (returnResponse) {
					return {
						errorCode: err?.response?.status ?? 500 | 400,
						errorData: err?.response?.data ?? {
							message: MESSAGE.ERROR,
						},
					};
				}
				if (err?.response?.status && err?.response?.data) {
                    const errorMessage = err.response.data.message;
                    // Skip showing the notification if the message contains "logged in required"
                    if (errorMessage?.includes('logged in required') || errorMessage?.includes('Unauthorized user.')) {
                        return;
                    }
                    errorNotification(`${err.response.data.message ?? MESSAGE.ERROR}`);
                }
				return null;
			} finally {
				if (updateState) {
					setLoading(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[updateState, postConfig, returnResponse, errorNotification]
	);

	const formData = useCallback(
		async (url: string, requestJSON: any) => {
			setLoading(true);
			try {
				const response = await fetch(API_HOST + url, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						accountMode: sandbox ? 'sandbox' : 'live',
					},
					body: requestJSON,
				});
				const apiData = await response.json();
				if (returnResponse) {
					return { response, apiData };
				}
				if (updateState) {
					setStatus(response?.ok);
					setIsLoaded(true);
					setData(apiData);
					setLoading(false);
				}
				return apiData;
			} catch (err: any) {
				// This code has been commented out for now because we are handling this functionality in the update-user-token hooks. We might use it in the future, so it has been retained here as a reference.
				// if (err?.response?.status === 401 || err?.response?.status === 403) {
				// 	await moveToLoginPage();
				// 	deleteCookie('user');
				// 	deleteCookie('userEmail');
				// 	deleteRefreshCookie("auth_refresh_token");
				// 	localStorage.clear();
				// 	window.location.href = '/login';
				// }
				if (returnResponse) {
					return {
						errorCode: err?.response?.status ?? 500 | 400,
						errorData: err?.response?.data ?? {
							message: MESSAGE.ERROR,
						},
					};
				}
				if (updateState) {
					setError(err);
				}
				return null;
			} finally {
				if (updateState) {
					setLoading(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[accessToken, returnResponse, sandbox, updateState]
	);

	const formDataWithAxios = useCallback(
		async (url: string, requestJSON: any) => {
			const source = axios.CancelToken.source();
			setCancelTokenSource(source);
			try {
				const response = await axios.post(
					API_HOST + url, requestJSON,
					{
						headers: {
							Authorization: `Bearer ${accessToken}`,
						},
						cancelToken: source.token,
						onUploadProgress: (data: any) => {
							setFileOploading(Math.round((100 * data.loaded) / data.total));
						},
					}
				);
				const apiData = await response;
				if (returnResponse) {
					return { response, apiData };
				}
				if (updateState) {
					setStatus(response?.ok);
					setIsLoaded(true);
					setData(apiData);
					setLoading(false);
				}
				return apiData;
			} catch (err: any) {
				// This code has been commented out for now because we are handling this functionality update-user-token hooks. We might use it in the future, so it has been retained here as a reference.
				// if (err?.response?.status === 401 || err?.response?.status === 403) {
				// 	await moveToLoginPage();
				// 	deleteCookie('user');
				// 	deleteCookie('userEmail');
				// 	deleteRefreshCookie("auth_refresh_token");
				// 	localStorage.clear();
				// 	window.location.href = '/login';
				// }
				if (returnResponse) {
					return {
						errorCode: err?.response?.status ?? 500 | 400,
						errorData: err?.response?.data ?? {
							message: MESSAGE.ERROR,
						},
					};
				}
				if (updateState) {
					setError(err);
				}
				return null;
			} finally {
				if (updateState) {
					setFileOploading(0);
					setLoading(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[accessToken, returnResponse, updateState]
	);

	const put = useCallback(
		async (url: string, requestJSON?: any) => {
			try {
				const response = await retryOperation(
					() => fetch(API_HOST + url, {
						method: 'PUT',
						...postConfig,
						body: JSON.stringify(requestJSON),
					}
						)
				);
				if (updateState) {
					setStatus(response?.ok);
				}
				const apiData = await response.json();
				if (updateState) {
					setStatus(response.status);
					setIsLoaded(true);
					setData(apiData.data);
				}
				return apiData.data;
			} catch (err: any) {
				// This code has been commented out for now because we are handling this functionality update-user-token hooks. We might use it in the future, so it has been retained here as a reference.
				// if (err?.response?.status === 401 || err?.response?.status === 403) {
				// 	await moveToLoginPage();
				// 	deleteCookie('user');
				// 	deleteCookie('userEmail');
				// 	deleteRefreshCookie("auth_refresh_token");
				// 	localStorage.clear();
				// 	window.location.href = '/login';
				// }

				if (updateState) {
					setError(err);
				}
				return null;
			} finally {
				if (updateState) {
					setLoading(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[deleteCookie, postConfig, updateState]
	);

	const remove = useCallback(
		async (url: string, requestJSON?: any) => {
			setLoading(true);
			try {
				const response = await retryOperation(
					() => fetch(API_HOST + url, {
						method: 'DELETE',
						...postConfig,
						body: JSON.stringify(requestJSON),
					})
				);
				if (updateState) {
					setStatus(response?.ok);
				}
				const apiData = await response.json();
				const { data: apiPayload } = apiData ?? {};
				if (returnResponse) {
					return { response: apiData, apiPayload };
				}
				if (updateState) {
					setStatus(response.status);
					setIsLoaded(true);
					setData(apiData.data);
				}
				return apiData.data;
			} catch (err: any) {
				// This code has been commented out for now because we are handling this functionality update-user-token hooks. We might use it in the future, so it has been retained here as a reference.
				// if (err?.response?.status === 401 || err?.response?.status === 403) {
				// 	await moveToLoginPage();
				// 	deleteCookie('user');
				// 	deleteCookie('userEmail');
				// 	deleteRefreshCookie("auth_refresh_token");
				// 	localStorage.clear();
				// 	window.location.href = '/login';
				// }

				if (updateState) {
					setError(err);
				}
				if (returnResponse) {
					return {
						errorCode: err?.response?.status ?? 500 | 400,
						errorData: err?.response?.data ?? {
							message: MESSAGE.ERROR,
						},
					};
				}
				return null;
			} finally {
				if (updateState) {
					setLoading(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[postConfig, updateState]
	);

	const patch = useCallback(
		/*
		 * `configHeader` mirrors `post` exactly — same flat headers object, same
		 * merge into `postConfig`. It exists because eVault is the first caller
		 * that has to send a DIFFERENT bearer than the session one on a PATCH:
		 * its endpoints take a short-lived, evault-scoped token (see
		 * hooks/use-vault-token), and the alternative — writing that token into
		 * `WebTokenState` — would swap the bearer for every request in the app.
		 * `get` and `post` already took the parameter; only `patch` did not.
		 */
		async (url: string, requestJSON?: any, configHeader?: any) => {
			if (configHeader) {
				postConfig.headers = {
					...postConfig.headers,
					...configHeader,
				};
			}
			if (updateState) {
				setLoading(true);
			}
			try {
				const response = await retryOperation(
					() => fetch(API_HOST + url, {
						method: 'PATCH',
						...postConfig,
						body: JSON.stringify(requestJSON),
					})
				);
				if (updateState) {
					setStatus(response?.ok);
				}
				const apiData = await response.json();
				if (updateState) {
					setIsLoaded(true);
				}
				const apiResponse = apiData.data ?? apiData;
				if (updateState) {
					setData(apiResponse);
				}
				return apiData.data ?? apiData;
			} catch (err: any) {
				// This code has been commented out for now because we are handling this functionality update-user-token hooks. We might use it in the future, so it has been retained here as a reference.
				// if (err?.response?.status === 401 || err?.response?.status === 403) {
				// 	await moveToLoginPage();
				// 	deleteCookie('user');
				// 	deleteCookie('userEmail');
				// 	deleteRefreshCookie("auth_refresh_token");
				// 	localStorage.clear();
				// 	window.location.href = '/login';
				// }
				if (updateState) {
					setError(err);
				}
				return null;
			} finally {
				if (updateState) {
					setLoading(false);
				}
			}
		},
		// eslint-disable-next-line
		[postConfig, accessToken]
	);

	return {
		get,
		post,
		formData,
		put,
		data,
		status,
		error,
		loading,
		setLoading,
		remove,
		patch,
		isLoaded,
		setIsLoaded,
		response,
		accessToken,
		abortController,
		sandbox,
		formDataWithAxios,
		fileUploading,
		stopUploadingFile,
	};
};
